import React, { useContext, useMemo, useState, useEffect } from 'react';
import { ShopContext, resolveProductImage } from '../context/ShopContext';
import { StarIcon } from './Icons';
import { formatINR } from '../utils/currency';
import './FloatingShopCollage.css';

const DEFAULT_COLLAGE_PRODUCTS = [
  {
    id: 'p1',
    name: 'Espeon Edition Sports Jersey',
    price: 1499,
    rating: 5,
    reviews: 84,
    image: '/images/cat-slide-sports-jersey.webp',
    imageUrl: '/images/cat-slide-sports-jersey.webp',
    tag: 'NEW DROP'
  },
  {
    id: 'p2',
    name: 'Midnight Silk Knit Polo',
    price: 2899,
    rating: 5,
    reviews: 120,
    image: '/images/cat-slide-men-classic.webp',
    imageUrl: '/images/cat-slide-men-classic.webp',
    tag: 'BEST SELLER'
  },
  {
    id: 'p3',
    name: 'Urban Heavyweight Fleece Hoodie',
    price: 4499,
    rating: 5,
    reviews: 161,
    image: '/images/cat-slide-men-hoodie.webp',
    imageUrl: '/images/cat-slide-men-hoodie.webp',
    tag: 'TRENDING'
  },
  {
    id: 'p4',
    name: 'Oxford Structured Drape Shirt',
    price: 1899,
    rating: 5,
    reviews: 95,
    image: '/images/cat-slide-women-classic.webp',
    imageUrl: '/images/cat-slide-women-classic.webp',
    tag: 'ESSENTIAL'
  },
  {
    id: 'p5',
    name: 'Unfold Signature Performance Polo',
    price: 1999,
    rating: 5,
    reviews: 110,
    image: '/images/cat-slide-women-hoodies.webp',
    imageUrl: '/images/cat-slide-women-hoodies.webp',
    tag: 'HOT'
  }
];

const FloatingShopCollage = () => {
  const { products, setView } = useContext(ShopContext);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [productOffset, setProductOffset] = useState(0);
  const [slideDirection, setSlideDirection] = useState('right');

  // Available product pool (combining uploaded products from context and fallback defaults)
  const productPool = useMemo(() => {
    const list = Array.isArray(products) && products.length > 0 ? products : [];
    if (list.length === 0) return DEFAULT_COLLAGE_PRODUCTS;
    if (list.length < 4) {
      const combined = [...list];
      DEFAULT_COLLAGE_PRODUCTS.forEach(def => {
        if (!combined.some(p => p.id === def.id || p.name === def.name)) {
          combined.push(def);
        }
      });
      return combined;
    }
    return list;
  }, [products]);

  // 15-second automatic sliding from left to right to cycle uploaded products
  useEffect(() => {
    if (!productPool || productPool.length === 0) return;

    const interval = setInterval(() => {
      setSlideDirection('right');
      setProductOffset(prev => (prev + 1) % productPool.length);
    }, 15000); // 15 seconds

    return () => clearInterval(interval);
  }, [productPool]);

  const slideNext = () => {
    setSlideDirection('right');
    setProductOffset(prev => (prev + 1) % productPool.length);
  };

  const slidePrev = () => {
    setSlideDirection('left');
    setProductOffset(prev => (prev - 1 + productPool.length) % productPool.length);
  };

  const total = productPool.length;
  const p1 = productPool[productOffset % total] || DEFAULT_COLLAGE_PRODUCTS[0];
  const p2 = productPool[(productOffset + 1) % total] || DEFAULT_COLLAGE_PRODUCTS[1];
  const p3 = productPool[(productOffset + 2) % total] || DEFAULT_COLLAGE_PRODUCTS[2];
  const p4 = productPool[(productOffset + 3) % total] || DEFAULT_COLLAGE_PRODUCTS[3];

  return (
    <section className="floating-shop-section container reveal-el">
      <div className="floating-shop-stage">
        
        {/* Background Atmosphere Glow */}
        <div className="floating-ambient-glow" />

        {/* Left Arrow Slide Button */}
        <button 
          type="button"
          onClick={slidePrev} 
          className="collage-slide-nav-btn nav-btn-left glass"
          title="Slide Left"
          aria-label="Previous Slide"
        >
          ‹
        </button>

        {/* Right Arrow Slide Button */}
        <button 
          type="button"
          onClick={slideNext} 
          className="collage-slide-nav-btn nav-btn-right glass"
          title="Slide Right"
          aria-label="Next Slide"
        >
          ›
        </button>

        {/* Dynamic Left-to-Right Animated Content Deck */}
        <div 
          key={productOffset} 
          className={`floating-slide-deck ${slideDirection === 'right' ? 'slide-anim-left-to-right' : 'slide-anim-right-to-left'}`}
        >
          {/* 1. Left Floating Product Card (Card 1) */}
          <div 
            className={`floating-item float-card-left ${hoveredCard === 'left' ? 'is-active' : ''}`}
            onClick={() => setView('product-detail', p1)}
            onMouseEnter={() => setHoveredCard('left')}
            onMouseLeave={() => setHoveredCard(null)}
            title={`Shop ${p1.name}`}
          >
            <div className="floating-card-body">
              <div className="floating-card-img-wrap">
                <img 
                  src={resolveProductImage(p1)} 
                  alt={p1.name} 
                  className="floating-card-img hd-clear-img" 
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="500"
                  onError={(e) => { e.target.src = '/images/cat-slide-men-polo.JPG'; }}
                />
                <span className="floating-pill-tag">{p1.tag || 'NEW DROP'}</span>
              </div>
              <div className="floating-card-info">
                <h4 className="floating-card-title">{p1.name}</h4>
                <div className="floating-card-rating">
                  <div className="stars-cluster-mini">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <StarIcon key={i} fill="#f59e0b" className="star-filled" size={12} />
                    ))}
                  </div>
                  <span className="floating-reviews-count">({p1.reviews || 84})</span>
                </div>
                <span className="floating-card-price">{formatINR(p1.price)}</span>
              </div>
            </div>
          </div>

          {/* 2. Center 3D Floating Showcase Composition (HD Crystal-Clear Cards) */}
          <div className="floating-center-composition">
            <div 
              className="center-showcase-card card-showcase-left glass" 
              onClick={() => setView('product-detail', p2)}
              title={`View ${p2.name}`}
            >
              <div className="center-card-img-wrap">
                <img 
                  src={resolveProductImage(p2)} 
                  alt={p2.name} 
                  className="center-card-img hd-clear-img"
                  loading="lazy"
                  decoding="async"
                  width="360"
                  height="480"
                  onError={(e) => { e.target.src = '/images/cat-slide-men-classic.JPG'; }}
                />
                <span className="center-card-pill">{p2.tag || 'PREMIUM'}</span>
              </div>
              <div className="center-card-footer">
                <h4 className="center-card-name">{p2.name}</h4>
                <span className="center-card-price">{formatINR(p2.price)}</span>
              </div>
            </div>

            <div 
              className="center-showcase-card card-showcase-right glass" 
              onClick={() => setView('product-detail', p3)}
              title={`View ${p3.name}`}
            >
              <div className="center-card-img-wrap">
                <img 
                  src={resolveProductImage(p3)} 
                  alt={p3.name} 
                  className="center-card-img hd-clear-img"
                  loading="lazy"
                  decoding="async"
                  width="360"
                  height="480"
                  onError={(e) => { e.target.src = '/images/cat-slide-men-hoodie.JPG'; }}
                />
                <span className="center-card-pill tag-hot">{p3.tag || 'HOT DROP'}</span>
              </div>
              <div className="center-card-footer">
                <h4 className="center-card-name">{p3.name}</h4>
                <span className="center-card-price">{formatINR(p3.price)}</span>
              </div>
            </div>
          </div>

          {/* 3. Right Floating Product Card (Card 2) */}
          <div 
            className={`floating-item float-card-right ${hoveredCard === 'right' ? 'is-active' : ''}`}
            onClick={() => setView('product-detail', p4)}
            onMouseEnter={() => setHoveredCard('right')}
            onMouseLeave={() => setHoveredCard(null)}
            title={`Shop ${p4.name}`}
          >
            <div className="floating-card-body">
              <div className="floating-card-img-wrap">
                <img 
                  src={resolveProductImage(p4)} 
                  alt={p4.name} 
                  className="floating-card-img hd-clear-img" 
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="500"
                  onError={(e) => { e.target.src = '/images/cat-slide-women-classic.JPG'; }}
                />
                <span className="floating-pill-tag tag-trending">{p4.tag || 'HOT'}</span>
              </div>
              <div className="floating-card-info">
                <h4 className="floating-card-title">{p4.name}</h4>
                <div className="floating-card-rating">
                  <div className="stars-cluster-mini">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <StarIcon key={i} fill="#f59e0b" className="star-filled" size={12} />
                    ))}
                  </div>
                  <span className="floating-reviews-count">({p4.reviews || 161})</span>
                </div>
                <span className="floating-card-price">{formatINR(p4.price)}</span>
              </div>
            </div>
          </div>

          {/* 4. Right Mid Floating Aesthetic Textile Pill */}
          <div 
            className="floating-item float-pill-right"
            onClick={() => setView('product-detail', p4)}
            title={`Explore ${p4.name}`}
          >
            <div className="textile-preview-pill">
              <img 
                src={resolveProductImage(p4)} 
                alt={p4.name} 
                className="pill-thumb-img hd-clear-img" 
                loading="lazy"
                decoding="async"
                width="64"
                height="64"
                onError={(e) => { e.target.src = '/images/cat-slide-women-classic.JPG'; }}
              />
              <div className="textile-pill-text">
                <span className="textile-name">{p4.name}</span>
                <span className="textile-gsm">380 GSM Organic</span>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Progress Dots Indicator */}
        <div className="collage-slide-dots">
          {productPool.slice(0, Math.min(6, productPool.length)).map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`collage-dot ${idx === (productOffset % Math.min(6, productPool.length)) ? 'active' : ''}`}
              onClick={() => {
                setSlideDirection(idx > (productOffset % 6) ? 'right' : 'left');
                setProductOffset(idx);
              }}
              aria-label={`Slide to product set ${idx + 1}`}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default FloatingShopCollage;
